import { useState, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useShippings, useAllAccounts } from '@/features/shippings/hooks';
import { shippingKeys } from '@/features/shippings/hooks/queryKeys';
import { ShippingsFilters as ShippingsFiltersBar } from '@/features/shippings/components/ShippingsFilters';
import { ShippingsTable, COLUMNS } from '@/features/shippings/components/ShippingsTable';
import type { ShippingsFilters as ShippingsFiltersType, ShippingStatus } from '@/features/shippings/types';

const DEFAULT_STATUSES: ShippingStatus[] = ['verified', 'shipped', 'received'];

export default function PackingSlipsListPage() {
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<ShippingStatus[]>(DEFAULT_STATUSES);
  const [shippingCompany, setShippingCompany] = useState('all');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [sortKey, setSortKey] = useState<ShippingsFiltersType['sortKey']>('createdAt');
  const [sortDirection, setSortDirection] = useState<1 | -1>(-1);

  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const setColumnFilter = (key: string, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    const timer = setTimeout(() => { setDebouncedSearch(search); setPage(0); }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const filters: ShippingsFiltersType = {
    search: debouncedSearch || undefined,
    status_in: selectedStatuses.length > 0 ? selectedStatuses : undefined,
    shippingCompany: shippingCompany !== 'all' ? shippingCompany : undefined,
    sortKey,
    sortDirection,
    pageNumber: page,
    pageLength: pageSize,
  };

  const shippingsQuery = useShippings(filters);
  const rawShippings = shippingsQuery.data?.packingSlips || [];
  const total = shippingsQuery.data?.total || 0;

  const accountsQuery = useAllAccounts();
  const accountOptions = useMemo(() => {
    const accounts = accountsQuery.data || [];
    const seen = new Set<string>();
    return accounts.reduce<{ label: string; value: string }[]>((acc, a) => {
      if (!seen.has(a.name)) {
        seen.add(a.name);
        acc.push({ label: a.name, value: a.name });
      }
      return acc;
    }, []);
  }, [accountsQuery.data]);

  const searchableSelectOptions = useMemo(
    () => ({ accountName: accountOptions }),
    [accountOptions],
  );

  const filteredShippings = useMemo(() => {
    const activeFilters = Object.entries(columnFilters).filter(([, v]) => v);
    if (activeFilters.length === 0) return rawShippings;

    return rawShippings.filter((shipping) =>
      activeFilters.every(([colKey, filterValue]) => {
        const col = COLUMNS.find((c) => c.key === colKey);
        if (!col || col.noFilter) return true;
        const rawValue = col.getValue(shipping);
        if (col.filterType === 'select' || col.filterType === 'searchable-select') {
          try {
            const selected = JSON.parse(filterValue) as string[];
            return selected.length === 0 || selected.includes(String(rawValue || ''));
          } catch { return true; }
        }
        return String(rawValue ?? '').toLowerCase().includes(filterValue.toLowerCase());
      }),
    );
  }, [rawShippings, columnFilters]);

  const hasActiveFilters =
    search !== '' ||
    JSON.stringify(selectedStatuses) !== JSON.stringify(DEFAULT_STATUSES) ||
    shippingCompany !== 'all';

  const handleSort = (key: ShippingsFiltersType['sortKey']) => {
    if (key === sortKey) setSortDirection(sortDirection === 1 ? -1 : 1);
    else { setSortKey(key); setSortDirection(-1); }
    setPage(0);
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: shippingKeys.all });
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title">
            Shipping <small>Track packing slips and deliveries</small>
          </h1>
        </div>
        <Button variant="outline" size="sm" onClick={handleRefresh} disabled={shippingsQuery.isFetching}>
          <RefreshCw className={`h-4 w-4 mr-1 ${shippingsQuery.isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <div className="flex items-center justify-between gap-4">
        <ShippingsFiltersBar
          search={search}
          onSearchChange={setSearch}
          selectedStatuses={selectedStatuses}
          onStatusChange={(s) => { setSelectedStatuses(s); setPage(0); }}
          shippingCompany={shippingCompany}
          onCompanyChange={(v) => { setShippingCompany(v); setPage(0); }}
          pageSize={pageSize}
          onPageSizeChange={(v) => { setPageSize(v); setPage(0); }}
          onClearAll={() => { setSearch(''); setSelectedStatuses(DEFAULT_STATUSES); setShippingCompany('all'); setPage(0); }}
          hasActiveFilters={hasActiveFilters}
        />
        <span className="text-sm text-muted-foreground whitespace-nowrap">
          {total} shipping{total !== 1 ? 's' : ''}
        </span>
      </div>

      {shippingsQuery.error ? (
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-red-500">Error loading shippings. Please try again.</p>
          </CardContent>
        </Card>
      ) : (
        <ShippingsTable
          shippings={filteredShippings}
          pageItemCount={rawShippings.length}
          total={total}
          sortKey={sortKey}
          sortDirection={sortDirection}
          onSort={handleSort}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          columnFilters={columnFilters}
          onColumnFilterChange={setColumnFilter}
          isLoading={shippingsQuery.isLoading}
          searchableSelectOptions={searchableSelectOptions}
        />
      )}
    </div>
  );
}
